import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Save, User, Mail, Phone, MapPin, Instagram, Facebook, PinIcon as Pinterest, FileText, Tags, Upload, BarChart3, Plus, Trash2, Camera } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { photographerAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const AdminSettings = () => {
  const [photographerInfo, setPhotographerInfo] = useState({
    name: '',
    tagline: '',
    short_bio: '',
    bio: '',
    email: '',
    phone: '',
    location: '',
    socialMedia: {
      instagram: '',
      facebook: '',
      pinterest: ''
    },
    hero_image_url: '',
    about_image_url: '',
    philosophy_text: '',
    equipment_cameras: '',
    equipment_lenses: '',
    equipment_accessories: '',
    stat_shootings: '',
    stat_awards: '',
    stat_clients: '',
    stat_experience: '',
    expertise_title: 'Meine Expertise',
    expertise_items: []
  });
  const [impressum, setImpressum] = useState('');
  const [datenschutz, setDatenschutz] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [uploadingAbout, setUploadingAbout] = useState(false);
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const defaultExpertiseItems = [
    { title: 'Portraits', description: 'Portraetfotografie fuer Business, Familie und persoenliche Anlaesse', image_url: '' },
    { title: 'Landschaft', description: 'Kunstvolle Naturaufnahmen und Landschaftsbilder fuer Ihr Zuhause', image_url: '' },
    { title: 'Events', description: 'Festhalten besonderer Momente bei Hochzeiten, Geburtstagen und Events', image_url: '' }
  ];
  const [uploadingExpertiseIndex, setUploadingExpertiseIndex] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await photographerAPI.getInfo();
      const info = response.info;
      
      // If no expertise items exist, use defaults
      if (!info.expertise_items || info.expertise_items.length === 0) {
        info.expertise_items = defaultExpertiseItems;
      }
      if (!info.expertise_title) {
        info.expertise_title = 'Meine Expertise';
      }
      
      setPhotographerInfo(info);
      setImpressum(info.impressum || '');
      setDatenschutz(info.datenschutz || '');
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast({
        title: "Fehler",
        description: "Einstellungen konnten nicht geladen werden",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Wird geladen...</p>
      </div>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPhotographerInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSocialMediaChange = (e) => {
    const { name, value } = e.target;
    setPhotographerInfo(prev => ({
      ...prev,
      socialMedia: {
        ...prev.socialMedia,
        [name]: value
      }
    }));
  };

  const handleImageUpload = async (file, imageType) => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('image_type', imageType);

    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/website-images/upload`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Update local state with new image URL
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.image_url}`;
      setPhotographerInfo(prev => ({
        ...prev,
        [`${imageType}_image_url`]: fullUrl
      }));

      toast({
        title: "Hochgeladen!",
        description: `${imageType === 'hero' ? 'Hero' : 'About'}-Bild wurde erfolgreich hochgeladen`
      });

      return true;
    } catch (error) {
      console.error('Image upload error:', error);
      toast({
        title: "Fehler",
        description: "Bild konnte nicht hochgeladen werden",
        variant: "destructive"
      });
      return false;
    }
  };

  const handleHeroImageSelect = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadingHero(true);
      await handleImageUpload(file, 'hero');
      setUploadingHero(false);
    }
  };

  const handleAboutImageSelect = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadingAbout(true);
      await handleImageUpload(file, 'about');
      setUploadingAbout(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await photographerAPI.updateInfo({
        ...photographerInfo,
        impressum,
        datenschutz
      });
      
      toast({
        title: "Gespeichert!",
        description: "Alle Änderungen wurden erfolgreich gespeichert",
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: "Fehler",
        description: "Änderungen konnten nicht gespeichert werden",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/upload')}
            className="text-gray-600 hover:text-gray-900 mb-4"
          >
            ← Zurück zum Dashboard
          </button>
          <h1 className="text-3xl font-heading font-bold text-gray-900">Website-Einstellungen</h1>
          <p className="text-gray-600 mt-2">Passen Sie alle Inhalte Ihrer Website an</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Information */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-heading font-semibold mb-6 flex items-center gap-2">
              <User size={24} />
              Persönliche Informationen
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={photographerInfo.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none"
                  placeholder="Ihr Name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tagline / Slogan
                </label>
                <input
                  type="text"
                  name="tagline"
                  value={photographerInfo.tagline}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none"
                  placeholder="z.B. Capturing Moments, Creating Memories"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kurze Beschreibung (wird auf der Startseite angezeigt)
                </label>
                <textarea
                  name="short_bio"
                  value={photographerInfo.short_bio}
                  onChange={handleChange}
                  rows="2"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none resize-none"
                  placeholder="Kurze Beschreibung fuer die Startseite (ca. 1-2 Saetze)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vollstaendige Bio (wird auf der 'Ueber Mich' Seite angezeigt)
                </label>
                <textarea
                  name="bio"
                  value={photographerInfo.bio}
                  onChange={handleChange}
                  rows="6"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none resize-none"
                  placeholder="Ausfuehrliche Beschreibung ueber sich und Ihre Fotografie..."
                />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-heading font-semibold mb-6 flex items-center gap-2">
              <Mail size={24} />
              Kontaktinformationen
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Mail size={16} className="inline mr-2" />
                  E-Mail
                </label>
                <input
                  type="email"
                  name="email"
                  value={photographerInfo.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none"
                  placeholder="ihre.email@beispiel.de"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Phone size={16} className="inline mr-2" />
                  Telefonnummer
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={photographerInfo.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none"
                  placeholder="+49 123 456789"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin size={16} className="inline mr-2" />
                  Standort
                </label>
                <input
                  type="text"
                  name="location"
                  value={photographerInfo.location}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none"
                  placeholder="Stadt, Land"
                />
              </div>
            </div>
          </div>

          {/* Social Media */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-heading font-semibold mb-6 flex items-center gap-2">
              <Instagram size={24} />
              Social Media Links
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Instagram size={16} className="inline mr-2" />
                  Instagram URL
                </label>
                <input
                  type="url"
                  name="instagram"
                  value={photographerInfo.socialMedia.instagram}
                  onChange={handleSocialMediaChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none"
                  placeholder="https://instagram.com/username"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Facebook size={16} className="inline mr-2" />
                  Facebook URL
                </label>
                <input
                  type="url"
                  name="facebook"
                  value={photographerInfo.socialMedia.facebook}
                  onChange={handleSocialMediaChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none"
                  placeholder="https://facebook.com/page"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Pinterest size={16} className="inline mr-2" />
                  Pinterest URL
                </label>
                <input
                  type="url"
                  name="pinterest"
                  value={photographerInfo.socialMedia.pinterest}
                  onChange={handleSocialMediaChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none"
                  placeholder="https://pinterest.com/username"
                />
              </div>
            </div>
          </div>

          {/* Bilder */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-heading font-semibold mb-6 flex items-center gap-2">
              <Upload size={24} />
              Website-Bilder
            </h2>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Startseite Hero-Bild
                </label>
                {photographerInfo.hero_image_url && (
                  <div className="mb-3">
                    <img 
                      src={photographerInfo.hero_image_url} 
                      alt="Hero Preview" 
                      className="w-full h-48 object-cover rounded-lg"
                    />
                  </div>
                )}
                <label className="flex flex-col items-center justify-center w-full h-32 px-6 py-4 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:border-primary hover:bg-gray-50 transition-all">
                  <Upload className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">
                    {uploadingHero ? 'Wird hochgeladen...' : 'Klicken zum Hochladen'}
                  </span>
                  <input
                    type="file"
                    className="hidden"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleHeroImageSelect}
                    disabled={uploadingHero}
                  />
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Bild fuer die Startseite (rechts neben dem Text)
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ueber Mich Seite Bild
                </label>
                {photographerInfo.about_image_url && (
                  <div className="mb-3">
                    <img 
                      src={photographerInfo.about_image_url} 
                      alt="About Preview" 
                      className="w-full h-48 object-cover rounded-lg"
                    />
                  </div>
                )}
                <label className="flex flex-col items-center justify-center w-full h-32 px-6 py-4 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:border-primary hover:bg-gray-50 transition-all">
                  <Upload className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">
                    {uploadingAbout ? 'Wird hochgeladen...' : 'Klicken zum Hochladen'}
                  </span>
                  <input
                    type="file"
                    className="hidden"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleAboutImageSelect}
                    disabled={uploadingAbout}
                  />
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Ihr Foto auf der Ueber Mich Seite
                </p>
              </div>
            </div>
          </div>

          {/* Philosophie */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-heading font-semibold mb-6 flex items-center gap-2">
              <FileText size={24} />
              Meine Philosophie
            </h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Philosophie-Text (wird auf der Ueber Mich Seite angezeigt)
              </label>
              <textarea
                name="philosophy_text"
                value={photographerInfo.philosophy_text}
                onChange={handleChange}
                rows="6"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none resize-none"
                placeholder="Beschreiben Sie Ihre fotografische Philosophie..."
              />
            </div>
          </div>

          {/* Ausruestung */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-heading font-semibold mb-6 flex items-center gap-2">
              <FileText size={24} />
              Professionelle Ausruestung
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kameras
                </label>
                <textarea
                  name="equipment_cameras"
                  value={photographerInfo.equipment_cameras}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none resize-none"
                  placeholder="Eine Kamera pro Zeile, z.B.&#10;Canon EOS R5&#10;Sony A7 IV&#10;Backup-Systeme"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Objektive
                </label>
                <textarea
                  name="equipment_lenses"
                  value={photographerInfo.equipment_lenses}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none resize-none"
                  placeholder="Ein Objektiv pro Zeile, z.B.&#10;24-70mm f/2.8&#10;70-200mm f/2.8&#10;35mm & 85mm Festbrennweiten"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Zubehoer
                </label>
                <textarea
                  name="equipment_accessories"
                  value={photographerInfo.equipment_accessories}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none resize-none"
                  placeholder="Zubehoer pro Zeile, z.B.&#10;Professionelles Lichtsystem&#10;Reflektoren & Diffusoren&#10;Adobe Lightroom & Photoshop"
                />
              </div>
            </div>
          </div>

          {/* Statistiken */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-heading font-semibold mb-6 flex items-center gap-2">
              <BarChart3 size={24} />
              Statistiken (Ueber Mich Seite)
            </h2>
            <p className="text-gray-600 mb-4 text-sm">
              Diese Statistiken werden auf der "Ueber Mich" Seite angezeigt.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Shootings
                </label>
                <input
                  type="text"
                  name="stat_shootings"
                  value={photographerInfo.stat_shootings || ''}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none"
                  placeholder="z.B. 150+"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Auszeichnungen
                </label>
                <input
                  type="text"
                  name="stat_awards"
                  value={photographerInfo.stat_awards || ''}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none"
                  placeholder="z.B. 5"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Zufriedene Kunden
                </label>
                <input
                  type="text"
                  name="stat_clients"
                  value={photographerInfo.stat_clients || ''}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none"
                  placeholder="z.B. 100+"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Jahre Erfahrung
                </label>
                <input
                  type="text"
                  name="stat_experience"
                  value={photographerInfo.stat_experience || ''}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none"
                  placeholder="z.B. 3+"
                />
              </div>
            </div>
          </div>

          {/* Expertise Section */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-heading font-semibold mb-6 flex items-center gap-2">
              <Camera size={24} />
              Expertise-Bereich (Startseite)
            </h2>
            <p className="text-gray-600 mb-4 text-sm">
              Dieser Bereich wird auf der Startseite angezeigt.
            </p>
            
            {/* Section Title */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ueberschrift des Bereichs
              </label>
              <input
                type="text"
                name="expertise_title"
                value={photographerInfo.expertise_title || 'Meine Expertise'}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none"
                placeholder="z.B. Meine Expertise"
              />
            </div>

            {/* Expertise Items */}
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                Expertise-Eintraege
              </label>
              
              {(photographerInfo.expertise_items || []).map((item, index) => (
                <div key={index} className="border-2 border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <span className="text-sm font-medium text-gray-500">Eintrag {index + 1}</span>
                    <button
                      type="button"
                      onClick={() => {
                        const newItems = [...(photographerInfo.expertise_items || [])];
                        newItems.splice(index, 1);
                        setPhotographerInfo(prev => ({
                          ...prev,
                          expertise_items: newItems
                        }));
                      }}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                  <div className="space-y-3">
                    {/* Image Upload */}
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        {item.image_url ? (
                          <img 
                            src={item.image_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${item.image_url}` : item.image_url}
                            alt={item.title}
                            className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
                          />
                        ) : (
                          <div className="w-20 h-20 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center">
                            <Camera size={24} className="text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <label className="block text-sm text-gray-600 mb-1">Bild (rund, wird auf Startseite angezeigt)</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            
                            setUploadingExpertiseIndex(index);
                            const formData = new FormData();
                            formData.append('image', file);
                            formData.append('image_type', 'expertise');
                            
                            try {
                              const token = localStorage.getItem('authToken');
                              const response = await axios.post(
                                `${process.env.REACT_APP_BACKEND_URL}/api/website-images/upload`,
                                formData,
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              
                              const newItems = [...(photographerInfo.expertise_items || [])];
                              newItems[index] = { ...newItems[index], image_url: response.data.image_url };
                              setPhotographerInfo(prev => ({
                                ...prev,
                                expertise_items: newItems
                              }));
                              
                              toast({ title: "Bild hochgeladen!", description: "Das Bild wurde erfolgreich hochgeladen." });
                            } catch (error) {
                              toast({ title: "Fehler", description: "Bild konnte nicht hochgeladen werden.", variant: "destructive" });
                            } finally {
                              setUploadingExpertiseIndex(null);
                            }
                          }}
                          className="text-sm"
                          disabled={uploadingExpertiseIndex === index}
                        />
                        {uploadingExpertiseIndex === index && (
                          <p className="text-xs text-primary mt-1">Wird hochgeladen...</p>
                        )}
                      </div>
                    </div>
                    <input
                      type="text"
                      value={item.title}
                      onChange={(e) => {
                        const newItems = [...(photographerInfo.expertise_items || [])];
                        newItems[index] = { ...newItems[index], title: e.target.value };
                        setPhotographerInfo(prev => ({
                          ...prev,
                          expertise_items: newItems
                        }));
                      }}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none"
                      placeholder="Titel (z.B. Hundefotografie)"
                    />
                    <textarea
                      value={item.description}
                      onChange={(e) => {
                        const newItems = [...(photographerInfo.expertise_items || [])];
                        newItems[index] = { ...newItems[index], description: e.target.value };
                        setPhotographerInfo(prev => ({
                          ...prev,
                          expertise_items: newItems
                        }));
                      }}
                      rows="2"
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none resize-none"
                      placeholder="Beschreibung"
                    />
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={() => {
                  const newItems = [...(photographerInfo.expertise_items || []), { title: '', description: '', image_url: '' }];
                  setPhotographerInfo(prev => ({
                    ...prev,
                    expertise_items: newItems
                  }));
                }}
                className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-primary hover:text-primary transition-colors w-full justify-center"
              >
                <Plus size={20} />
                Neuen Eintrag hinzufuegen
              </button>
            </div>
          </div>

          {/* Impressum */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-heading font-semibold mb-6 flex items-center gap-2">
              <FileText size={24} />
              Impressum
            </h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Impressum-Text (wird auf der Impressum-Seite angezeigt)
              </label>
              <textarea
                value={impressum}
                onChange={(e) => setImpressum(e.target.value)}
                rows="10"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none resize-none font-mono text-sm"
                placeholder="Angaben gemäß § 5 TMG:\n\nName:\nAdresse:\nE-Mail:\nTelefon:\n\nVerantwortlich für den Inhalt nach § 55 Abs. 2 RStV:\n..."
              />
              <p className="text-xs text-gray-500 mt-2">
                Hinweis: Bitte fügen Sie hier Ihre vollständigen Impressum-Angaben gemäß den gesetzlichen Vorgaben ein.
              </p>
            </div>
          </div>

          {/* Datenschutzerklärung */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-heading font-semibold mb-6 flex items-center gap-2">
              <FileText size={24} />
              Datenschutzerklärung
            </h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Datenschutz-Text (DSGVO – wird auf /datenschutz angezeigt)
              </label>
              <textarea
                value={datenschutz}
                onChange={(e) => setDatenschutz(e.target.value)}
                rows="14"
                data-testid="admin-datenschutz-textarea"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none resize-none font-mono text-sm"
                placeholder="Datenschutzerklärung..."
              />
              <p className="text-xs text-gray-500 mt-2">
                Hinweis: Ändern Sie diesen Text nur, wenn sich auf der Seite technisch relevante Punkte ändern (z. B. neues Kontaktformular, Analyse-Tools, Newsletter). Ansonsten den generierten DSGVO-Text unverändert lassen.
              </p>
            </div>
          </div>

          {/* Kategorien-Management */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
              <Tags size={24} />
              Galerie-Kategorien
            </h2>
            <p className="text-gray-600 mb-4">
              Verwalten Sie Ihre Galerie-Kategorien (hinzufuegen, umbenennen, loeschen)
            </p>
            <button
              type="button"
              onClick={() => navigate('/admin/categories')}
              className="flex items-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <Tags size={20} />
              Kategorien verwalten
            </button>
          </div>

          {/* Services-Management */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
              <FileText size={24} />
              Services
            </h2>
            <p className="text-gray-600 mb-4">
              Verwalten Sie Ihre angebotenen Services (hinzufuegen, bearbeiten, loeschen)
            </p>
            <button
              type="button"
              onClick={() => navigate('/admin/services')}
              className="flex items-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <FileText size={20} />
              Services verwalten
            </button>
          </div>

          {/* Preise & Leistungen-Management */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
              <FileText size={24} />
              Preise &amp; Leistungen
            </h2>
            <p className="text-gray-600 mb-4">
              Verwalten Sie Ihre Preise und Leistungspakete (hinzufuegen, bearbeiten, loeschen). Die Seite ist über den "Preise ansehen"-Button auf der Services-Seite erreichbar.
            </p>
            <button
              type="button"
              onClick={() => navigate('/admin/preise')}
              data-testid="admin-settings-preise-btn"
              className="flex items-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <FileText size={20} />
              Preise verwalten
            </button>
          </div>

          {/* Testimonials-Management */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
              <FileText size={24} />
              Kundenbewertungen
            </h2>
            <p className="text-gray-600 mb-4">
              Verwalten Sie Kundenbewertungen, die auf der Startseite angezeigt werden
            </p>
            <button
              type="button"
              onClick={() => navigate('/admin/testimonials')}
              className="flex items-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <FileText size={20} />
              Bewertungen verwalten
            </button>
          </div>

          {/* Messages-Management */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
              <FileText size={24} />
              Kontaktanfragen
            </h2>
            <p className="text-gray-600 mb-4">
              Alle Nachrichten vom Kontaktformular anzeigen
            </p>
            <button
              type="button"
              onClick={() => navigate('/admin/messages')}
              className="flex items-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <FileText size={20} />
              Nachrichten anzeigen
            </button>
          </div>

          {/* Save Button */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/admin/upload')}
              className="px-6 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Save size={20} />
              {saving ? 'Wird gespeichert...' : 'Änderungen speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminSettings;
